import getFetcher from 'api/utils/getFetcher';
import { useMemo } from 'react';
import useSWR from 'swr';
import type { LegacyApiPlan, LegacyPlan } from 'types/legacyDashboard';
import { convertApiPlanToReduxPlan, reducePlans } from 'utils/legacy';

export interface PlansResult {
  success: true;
  data?: Array<LegacyApiPlan>;
  size: number;
  limit: number;
  lastid: PlanId;
  continues: boolean;
}

export interface PlansError {}

interface UsePlanProps {
  organizationId?: OrgId;
  canLoad?: boolean;
  includezdr?: boolean;
  allZones?: boolean;
}

const usePlans = ({
  organizationId,
  canLoad = true,
  includezdr = false,
  allZones = false,
}: UsePlanProps) => {
  const queryParams = new URLSearchParams({
    orgid: organizationId?.toString() ?? '',
    enabled: '1',
    partnerid: '1',
    limit: '1000',
    includezdr: includezdr ? '1' : '0',
  });

  const url = canLoad ? `${process.env.VITE_API_ROOT}/plans?${queryParams}` : null;

  const { data, error, isLoading, mutate, isValidating } = useSWR<PlansResult, PlansError>(
    url,
    getFetcher
  );

  const plans: Array<LegacyPlan> = useMemo(() => {
    if (!data?.data) {
      return [];
    }

    // Convert the plans data same as legacy dashboard does
    const apiPlans = structuredClone(data.data).filter((plan) => plan.groupid !== 0);
    const reduxPlans = convertApiPlanToReduxPlan(apiPlans);

    const newPlans = reduxPlans.reduce<Array<LegacyPlan>>(
      (acc, plan) => reducePlans(acc, plan),
      []
    );

    // Filter out duplicate plans by ID, keeping only one plan per ID when allZones is false
    const filteredNewPlans = [
      ...new Map<PlanId, LegacyPlan>(newPlans.map((plan) => [plan.id, plan])).values(),
    ];

    return (allZones ? newPlans : filteredNewPlans).sort((a, b) => {
      if (a.displayCategory === b.displayCategory) {
        return a.id - b.id;
      }

      return a.displayCategory === 'custom' ? -1 : 1;
    });
  }, [data, allZones]);

  return {
    plans,
    error,
    isLoading,
    mutate,
    isValidating,
  };
};

export default usePlans;
